import { createError, ErrorCodes, Logger, PlatformUtil } from '@quickplay/foundation';
import { AdPlaybackPolicy, Player } from '@quickplay/player-interface';
import { PALConfiguration } from '@quickplay/pal';

import { MediatailorInitializationData } from '../../model/MediatailorInitializationData';
import { MediatailorSession } from '../../model/MediatailorSession';
import { MediatailorMetadata, ReportingMode } from '../../model/MediatailorMetadata';

import { DatazoomFactory } from './DatazoomFactory';
import { DatazoomMediatailorPlayer } from './DatazoomMediatailorPlayer';
import { MediatailorErrorCodes } from '../MediatailorErrorCodes';
import { AdTrackingUpdateMode, DatazoomMediaTailorAdsResponse } from '../../model/DatazoomAdMetaData';
import { MediatailorPlayer } from '../../model/MediatailorPlayer';
import { StreamStartUpTimeData } from '../../model/StreamStartUpTimeData';
import { AdFrequencyControlConfiguration } from '@quickplay/player';

export function createClientMediatailorSession(): MediatailorSession {
  let datazoomSession: any;
  let adMetaData: DatazoomMediaTailorAdsResponse;

  const AWS_SESSION_ID = 'aws.sessionId';
  const DEFAULT_CONNECTION_TIMEOUT_MS = 8000;
  const DEFAULT_MAX_RETRIES = 2;
  let mediatailorSessionStartTime = 0;
  let mediatailorSessionEndTime = 0;

  let connectionTimeoutInMS: number;
  let maxRetries: number;

  /* ------------------------------------------------------------ */
  /* Helpers                                                      */
  /* ------------------------------------------------------------ */

  const extractSessionIdFromManifestUrl = (manifestUrl: string): string | undefined => {
    try {
      return new URL(manifestUrl).searchParams.get(AWS_SESSION_ID) ?? undefined;
    } catch {
      return undefined;
    }
  };

  const waitForAdMetadata = async (
    sdk: any,
    session: any,
    playbackUrl: string,
    sessionId?: string,
    fallbackUrl?: string,
  ): Promise<MediatailorMetadata> => {
    return new Promise(resolve => {
      let trackingInfoResponseCount = 0;
      let resolved = false;

      const cleanup = (): void => {
        session.removeEventListener(sdk.mediatailor.SessionUiEvent.AD_TRACKING_INFO_RESPONSE, onTrackingInfo);
        session.stopUpdatingAdTrackingInfo?.();
      };

      const resolveOnce = (metadata: MediatailorMetadata): void => {
        if (resolved) return;
        resolved = true;
        cleanup();
        resolve(metadata);
      };

      function onTrackingInfo() {
        const ads = session.getAllAds?.() ?? [];

        if (ads.length > 0) {
          adMetaData = ads;
          resolveOnce({
            manifestUrl: playbackUrl,
            adSessionId: sessionId,
            isFallback: false,
          });
          return;
        }

        trackingInfoResponseCount++;

        if (trackingInfoResponseCount >= maxRetries) {
          resolveOnce({
            manifestUrl: playbackUrl,
            adSessionId: sessionId,
            isFallback: false,
          });
          return;
        }

        // request tracking info again if needed
        session.updateAdTrackingInfo?.();
      }

      session.addEventListener(sdk.mediatailor.SessionUiEvent.AD_TRACKING_INFO_RESPONSE, onTrackingInfo);

      session.updateAdTrackingInfo?.();

      // DEV NOTE: Hard safety timeout to avoid indefinite startup blocking when
      // ad tracking metadata is not received from MediaTailor/Datazoom.
      setTimeout(() => {
        if (resolved) return;
        resolveOnce({
          manifestUrl: fallbackUrl ?? playbackUrl,
          adSessionId: sessionId,
          isFallback: true,
          fallbackReason: createError(
            MediatailorErrorCodes.TRACKING_API_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
            'Failed to fetch Ad metadata',
          ),
        });
      }, connectionTimeoutInMS);
    });
  };

  /* ------------------------------------------------------------ */
  /* Public API                                                   */
  /* ------------------------------------------------------------ */

  return {
    async initialize(
      initData: MediatailorInitializationData,
      palConfig?: PALConfiguration,
    ): Promise<MediatailorMetadata> {
      try {
        connectionTimeoutInMS = initData?.connectionTimeoutInMS || DEFAULT_CONNECTION_TIMEOUT_MS;
        maxRetries = initData?.maxRetries || DEFAULT_MAX_RETRIES;
        DatazoomFactory.initialize({
          configId: initData.mediatailorSessionConfiguration?.configId ?? '',
        });

        if (palConfig) {
          DatazoomFactory.initPal();
        }

        const sdk = DatazoomFactory.getSDK();
        const omsdkInfo = DatazoomFactory.getOMSDKPartnerInfo();
        const sessionConfig: Record<string, any> = {
          sessionInitUrl: initData.sessionUrl,
          adTrackingUpdateMode: initData.isLive
            ? (initData.mediatailorSessionConfiguration?.liveAdTrackingMode ?? AdTrackingUpdateMode.PAGINATION)
            : AdTrackingUpdateMode.FULL,
          playerParams: initData.mediatailorSessionConfiguration?.playerParams,
          originParams: {
            ...initData.mediatailorSessionConfiguration?.originParams,
            logMode: initData.mediatailorSessionConfiguration?.logMode,
          },
          sessionFeatures: {
            adSignaling: initData?.mediatailorSessionConfiguration?.adSignaling ? 'on' : 'off',
            extra: {
              ...initData.mediatailorSessionConfiguration?.extra,
            },
            overlayAvails: initData?.mediatailorSessionConfiguration?.overlayAvails ?? 'on',
            reportingMode: initData?.mediatailorSessionConfiguration?.reportingMode ?? ReportingMode.SERVER,
          },
          ...(palConfig && {
            palNonceRequestParams: {
              ...palConfig,
              omidPartnerName: omsdkInfo?.name,
              omidPartnerVersion: omsdkInfo?.version,
            },
          }),
        };
        mediatailorSessionStartTime = performance.now();
        datazoomSession = await sdk.mediatailor.createSession(sessionConfig);
        mediatailorSessionEndTime = performance.now();

        const playbackUrl = datazoomSession.getPlaybackUrl();
        const adSessionId = extractSessionIdFromManifestUrl(playbackUrl);

        if (initData.isLive || PlatformUtil.isApple()) {
          return {
            manifestUrl: playbackUrl,
            adSessionId,
            isFallback: false,
          };
        }

        // Warm the manifest before tracking
        await fetch(playbackUrl);

        return waitForAdMetadata(sdk, datazoomSession, playbackUrl, adSessionId, initData.fallbackUrl);
      } catch (error: any) {
        // DEV NOTE:
        // Ensure end time is captured even when MediaTailor session initialization fails.
        // Without this, ssaiStreamUrlFetchTimeInMS may become negative
        // (endTime would remain 0 while startTime was already set).
        // This is especially observed when session init fails.
        mediatailorSessionEndTime = performance.now();
        return {
          manifestUrl: initData.fallbackUrl,
          isFallback: true,
          fallbackReason: createError(
            MediatailorErrorCodes.SESSION_INIT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
            'Mediatailor Session initialization Failed',
            error,
          ),
        };
      }
    },

    createMediatailorPlayer(
      player: Player,
      metadata?: MediatailorMetadata,
      adPlaybackPolicy?: AdPlaybackPolicy,
      logger?: Logger,
      videoElement?: HTMLVideoElement,
      adFrequencyControl?: AdFrequencyControlConfiguration,
    ): MediatailorPlayer {
      const streamStartUpTimeData: StreamStartUpTimeData = {
        ssaiStreamUrlFetchTimeInMS: Number((mediatailorSessionEndTime - mediatailorSessionStartTime).toFixed(2)),
      };
      const playerMetadata = {
        ...metadata,
        adMetadata: adMetaData,
      };
      return new DatazoomMediatailorPlayer(
        player,
        datazoomSession,
        playerMetadata,
        adPlaybackPolicy,
        streamStartUpTimeData,
        logger,
        videoElement,
        adFrequencyControl,
      );
    },
  };
}
